import ReactMarkdown from 'react-markdown';
import { useState } from 'react';

interface MarkdownPreviewProps {
  content: string;
  title?: string;
}

export function MarkdownPreview({ content, title }: MarkdownPreviewProps) {
  const [activeView, setActiveView] = useState<'preview' | 'raw'>('preview');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!content) {
    return (
      <div className="flex-1 card p-8 flex flex-col items-center justify-center min-h-[400px]">
        {/* Empty state illustration */}
        <div className="mb-6">
          <div className="w-20 h-20 rounded-xl bg-surface-700 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-surface-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
        </div>

        <h3 className="text-lg font-medium text-surface-300 mb-2">Ready to transform</h3>
        <p className="text-surface-500 text-center max-w-sm">
          Paste a URL above and hit Transform to convert content into clean, readable markdown.
        </p>

        {/* Feature highlights */}
        <div className="grid grid-cols-3 gap-4 mt-8 w-full max-w-lg">
          <div className="text-center p-3">
            <div className="w-10 h-10 rounded-lg bg-surface-800 mx-auto mb-2 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p className="text-xs text-surface-500">Fast extraction</p>
          </div>
          <div className="text-center p-3">
            <div className="w-10 h-10 rounded-lg bg-surface-800 mx-auto mb-2 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
              </svg>
            </div>
            <p className="text-xs text-surface-500">Clean formatting</p>
          </div>
          <div className="text-center p-3">
            <div className="w-10 h-10 rounded-lg bg-surface-800 mx-auto mb-2 flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-xs text-surface-500">Multi-page support</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 card overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-surface-700 bg-surface-800 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-surface-200 truncate">
              {title || 'Markdown Preview'}
            </h3>
          </div>
        </div>

        {/* View toggle and copy button */}
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex p-1 bg-surface-800 rounded-lg">
            <button
              onClick={() => setActiveView('preview')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeView === 'preview'
                  ? 'bg-primary-500 text-white'
                  : 'text-surface-400 hover:text-surface-200'
              }`}
            >
              Preview
            </button>
            <button
              onClick={() => setActiveView('raw')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeView === 'raw'
                  ? 'bg-primary-500 text-white'
                  : 'text-surface-400 hover:text-surface-200'
              }`}
            >
              Raw
            </button>
          </div>

          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-all duration-200 ${
              copied
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-surface-800 text-surface-400 hover:text-surface-200 hover:bg-surface-700'
            }`}
            title="Copy to clipboard"
          >
            {copied ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeView === 'preview' ? (
          <div className="p-6 markdown-preview">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        ) : (
          <pre className="p-6 text-sm font-mono text-surface-300 whitespace-pre-wrap break-words">
            {content}
          </pre>
        )}
      </div>

      {/* Footer with stats */}
      <div className="px-4 py-2 border-t border-surface-700 bg-surface-800 flex items-center justify-between text-xs text-surface-500">
        <span>{content.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
        <span>{content.length.toLocaleString()} characters</span>
      </div>
    </div>
  );
}
